import { format, formatDistanceToNowStrict, isThisYear, isToday, isYesterday } from 'date-fns';
import type { Locale } from 'date-fns';
import { th as thLocale } from 'date-fns/locale';

/**
 * The active locale, as a module-level value rather than a parameter.
 *
 * These helpers are called from inside render in dozens of places, most of them
 * deep in a column definition where threading a language argument down would
 * mean rewriting the call site rather than the formatter. `I18nProvider` sets
 * this before it re-renders the tree, so anything that re-renders on a language
 * change - which is everything under the provider - reads the new value.
 */
let locale: Locale | undefined;
let relativeWords = { justNow: 'just now', ago: '{value} ago', yesterday: 'Yesterday' };

export function setFormatLocale(lang: 'en' | 'th'): void {
  locale = lang === 'th' ? thLocale : undefined;
  relativeWords =
    lang === 'th'
      ? { justNow: 'เมื่อสักครู่', ago: '{value}ที่แล้ว', yesterday: 'เมื่อวาน' }
      : { justNow: 'just now', ago: '{value} ago', yesterday: 'Yesterday' };
}

/**
 * Timestamp formatting rules, in one place because inconsistency here is the
 * fastest way to make a console feel unfinished.
 *
 *   today      -> 14:32
 *   yesterday  -> Yesterday 14:32
 *   this year  -> 3 Mar 14:32
 *   older      -> 3 Mar 2024
 */
export function timestamp(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';

  if (isToday(date)) return format(date, 'HH:mm', { locale });
  if (isYesterday(date)) return `${relativeWords.yesterday} ${format(date, 'HH:mm', { locale })}`;
  if (isThisYear(date)) return format(date, 'd MMM HH:mm', { locale });
  return format(date, 'd MMM yyyy', { locale });
}

export function fullTimestamp(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime())
    ? '—'
    : format(date, 'd MMM yyyy, HH:mm:ss', { locale });
}

export function relative(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '—';
  const seconds = (Date.now() - date.getTime()) / 1000;
  if (seconds < 10) return relativeWords.justNow;
  return relativeWords.ago.replace('{value}', formatDistanceToNowStrict(date, { locale }));
}

/** Compact counts for tiles: 1 234 -> 1.2k. Never lies about the order of magnitude. */
export function compactNumber(value: number): string {
  if (!Number.isFinite(value)) return '—';
  if (Math.abs(value) < 1000) return String(value);
  if (Math.abs(value) < 1_000_000) return `${(value / 1000).toFixed(value < 10_000 ? 1 : 0)}k`;
  return `${(value / 1_000_000).toFixed(1)}M`;
}

export function fullNumber(value: number): string {
  return new Intl.NumberFormat('en-GB').format(value);
}

export function bytes(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

export function duration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  const hours = Math.floor(seconds / 3600);
  if (hours < 24) return `${hours}h ${Math.floor((seconds % 3600) / 60)}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

export function latency(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  return ms < 1000 ? `${Math.round(ms)} ms` : `${(ms / 1000).toFixed(2)} s`;
}

/** Countdown used by the QR panel. */
export function secondsUntil(iso: string): number {
  return Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 1000));
}
